import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { errorMessage as toErrorMessage, reportError } from '../lib/errors';

/**
 * Where Stripe's hosted Express onboarding sends the trader back to.
 *
 * Two landing points, one component, because they differ only in what they do
 * on arrival:
 *
 *   /connect/complete  (`return_url`)  — the form was submitted, or the trader
 *       closed it. Stripe attaches no status to this redirect, and deliberately
 *       so: the only way to know is to ask. We call stripe-connect/status, which
 *       reads the live account and syncs the org's columns, then either
 *       celebrates, says "Stripe is still checking", or sends them back into the
 *       form for what is still missing.
 *
 *   /connect/reauth    (`refresh_url`) — the Account Link expired or was reused.
 *       Links are single-use and last minutes, so there is nothing to report:
 *       mint another one and send them straight back in.
 */
/** The signed-in user's active org, or null when the session has lapsed. */
async function activeOrgId(): Promise<string | null> {
    const { supabase } = await import('../lib/supabase');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    const { data: profile, error } = await supabase
        .from('profiles').select('active_org_id').eq('id', user.id).single();
    if (error) {
        reportError(error, 'connect-return.profiles');
        throw error;
    }
    return profile?.active_org_id ?? null;
}

/** Mint an Account Link and hand the browser to Stripe's hosted form. */
async function openOnboarding(orgId: string): Promise<void> {
    const { supabase } = await import('../lib/supabase');
    const { data, error } = await supabase.functions.invoke('stripe-connect/onboard', {
        body: { org_id: orgId }
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    if (!data?.url) throw new Error('Could not open the Stripe setup form.');
    window.location.href = data.url;
}

type Mode = 'complete' | 'reauth';

type Phase = 'checking' | 'done' | 'pending' | 'unfinished' | 'error';

export function ConnectReturn({ mode }: { mode: Mode }) {
    const navigate = useNavigate();
    const [phase, setPhase] = useState<Phase>('checking');
    const [message, setMessage] = useState('');
    const hasRun = useRef(false);

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;

        const run = async () => {
            const { supabase } = await import('../lib/supabase');

            const orgId = await activeOrgId();

            if (!orgId) {
                // Session gone — the hosted form can sit open for a long time.
                navigate('/login');
                return;
            }

            if (mode === 'reauth') {
                await openOnboarding(orgId);
                return;
            }

            const { data, error } = await supabase.functions.invoke('stripe-connect/status', {
                body: { org_id: orgId }
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);

            if (data?.onboarding_complete) {
                setPhase('done');
                // Hard reload so the settings panel re-reads the org row.
                setTimeout(() => { window.location.href = '/settings?payment_setup=complete'; }, 2200);
            } else if (data?.details_submitted) {
                setPhase('pending');
                setTimeout(() => { window.location.href = '/settings'; }, 4000);
            } else {
                setPhase('unfinished');
            }
        };

        run().catch((err) => {
            setPhase('error');
            setMessage(toErrorMessage(err) || 'We could not finish setting up payments.');
        });
    }, [mode, navigate]);

    const resumeOnboarding = async () => {
        setPhase('checking');
        try {
            const orgId = await activeOrgId();
            if (!orgId) {
                navigate('/login');
                return;
            }
            await openOnboarding(orgId);
        } catch (err) {
            setPhase('error');
            setMessage(toErrorMessage(err) || 'We could not reopen the Stripe setup form.');
        }
    };

    const copy: Record<Phase, { icon: ReactNode; tone: string; title: string; body: string }> = {
        checking: {
            icon: <Loader2 className="w-8 h-8 text-accent animate-spin" />,
            tone: 'bg-accent/10',
            title: mode === 'reauth' ? 'Reopening your setup' : 'Checking your payment setup',
            body: mode === 'reauth'
                ? 'That link had expired, so we are getting you a fresh one.'
                : 'One moment while we confirm everything with Stripe.',
        },
        done: {
            icon: <CheckCircle2 className="w-8 h-8 text-success" />,
            tone: 'bg-success/10 border border-success/20',
            title: "You're set up to take payments",
            body: 'Card payments are on. Money from invoices goes straight to your bank.',
        },
        pending: {
            icon: <Clock className="w-8 h-8 text-gold" />,
            tone: 'bg-gold-fill/10 border border-gold/20',
            title: 'Details received — Stripe is checking them',
            body: 'Nothing more to do. This usually takes a few minutes, and we will let you know the moment you can take payments.',
        },
        unfinished: {
            icon: <Clock className="w-8 h-8 text-gold" />,
            tone: 'bg-gold-fill/10 border border-gold/20',
            title: 'Setup is not finished yet',
            body: 'Stripe still needs a few details before you can take card payments.',
        },
        error: {
            icon: <XCircle className="w-8 h-8 text-danger" />,
            tone: 'bg-danger/10 border border-danger/20',
            title: 'Something went wrong',
            body: message,
        },
    };

    const view = copy[phase];

    return (
        <div className="min-h-screen bg-panel flex items-center justify-center p-4">
            <div className="bg-panel border border-line rounded-2xl p-6 sm:p-8 w-full max-w-md text-center shadow-2xl">
                <div className="flex flex-col items-center animate-in fade-in duration-300">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ${view.tone}`}>
                        {view.icon}
                    </div>
                    <h2 className="text-xl font-bold text-fg mb-2">{view.title}</h2>
                    <p className="text-sm text-fg-2">{view.body}</p>

                    {(phase === 'unfinished' || phase === 'error') && (
                        <div className="mt-6 w-full flex flex-col sm:flex-row gap-2">
                            <button
                                onClick={resumeOnboarding}
                                className="flex-1 min-h-[44px] px-4 py-2 fd-btn-primary rounded-[var(--fds-radius)] text-sm font-semibold transition-opacity"
                            >
                                Finish setup
                            </button>
                            <button
                                onClick={() => { window.location.href = '/settings'; }}
                                className="flex-1 min-h-[44px] px-4 py-2 bg-raised text-fg border border-line rounded-[var(--fds-radius)] text-sm font-medium hover:opacity-90 transition-opacity"
                            >
                                Do this later
                            </button>
                        </div>
                    )}

                    {(phase === 'done' || phase === 'pending') && (
                        <p className="text-xs text-fg-2 mt-6">Taking you back to settings…</p>
                    )}
                </div>
            </div>
        </div>
    );
}
